import { getApiUrl, getAuthHeaders } from './api-client';

/**
 * Получить список всех участников команды
 * GET /admin/team/members
 */
export async function getAdminTeam(params = {}) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return { items: [], total: 0, page: 1, limit: 20, totalPages: 0 };
        }

        const queryParams = new URLSearchParams();

        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== '') {
                const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                queryParams.append(key, stringValue);
            }
        }

        const queryString = queryParams.toString();
        const url = `${apiUrl}/admin/team/members${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            cache: 'no-store',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders(),
            }
        });

        if (!response.ok) {
            console.error(`Failed to fetch team members. Status: ${response.status}`);
            return { items: [], total: 0, page: 1, limit: 20, totalPages: 0 };
        }

        const data = await response.json();
        return {
            items: data.items || [],
            total: data.total || 0,
            page: data.page || 1,
            limit: data.limit || 20,
            totalPages: data.totalPages || Math.ceil((data.total || 0) / (data.limit || 20)),
            meta: data
        };
    } catch (error) {
        console.error("Error fetching admin team members:", error);
        return { items: [], total: 0, page: 1, limit: 20, totalPages: 0 };
    }
}

/**
 * Создать нового участника команды
 * POST /admin/team/members
 */
export async function createAdminTeamMember(memberData) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return null;
        }

        // Преобразуем поля формы в API формат
        // Если значение это строка, создаём объект {ua, en}
        const normalizeField = (value) => {
            if (!value) return { ua: '', en: '' };
            if (typeof value === 'object') return value;
            return { ua: value, en: value };
        };

        const payload = {
            id: memberData.id,
            slug: memberData.slug,
            fullName: normalizeField(memberData.fullName || memberData.name),
            role: normalizeField(memberData.role),
            expertise: normalizeField(memberData.expertise),
            bio: normalizeField(memberData.bio || memberData.description),
            photoUrl: memberData.photoUrl || memberData.photo || '',
            sortOrder: memberData.sortOrder || 0,
            isFeatured: memberData.isFeatured || false,
            isActive: memberData.isActive !== false,
        };

        const response = await fetch(`${apiUrl}/admin/team/members`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...getAuthHeaders(),
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error(`Failed to create team member. Status: ${response.status}`, data);
            return { error: true, ...data };
        }

        return data;
    } catch (error) {
        console.error("Error creating admin team member:", error);
        return { error: true, message: error.message };
    }
}

/**
 * Получить участника команды по id
 * GET /admin/team/members/{memberId}
 */
export async function getAdminTeamMemberById(memberId, params = {}) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            throw new Error('API URL is not configured');
        }

        const queryParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        }

        const queryString = queryParams.toString();
        const url = `${apiUrl}/admin/team/members/${memberId}${queryString ? `?${queryString}` : ''}`;


        const response = await fetch(url, {
            cache: 'no-store',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders(),
            }
        });

        const data = await response.json();

        if (!response.ok) {
            const errorMsg = data?.message || `Failed to fetch team member. Status: ${response.status}`;
            console.error(`Error fetching team member by id:`, errorMsg, data);
            throw new Error(errorMsg);
        }

        return data;
    } catch (error) {
        console.error("Error fetching admin team member by id:", error);
        throw error;
    }
}

/**
 * Обновить участника команды
 * PUT /admin/team/members/{memberId}
 */
export async function updateAdminTeamMember(memberId, memberData) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return null;
        }

        // Преобразуем поля формы в API формат
        // Если значение это строка, создаём объект {ua, en}
        const normalizeField = (value) => {
            if (!value) return { ua: '', en: '' };
            if (typeof value === 'object') return value;
            return { ua: value, en: value };
        };

        const payload = {
            id: memberData.id,
            slug: memberData.slug,
            fullName: normalizeField(memberData.fullName || memberData.name),
            role: normalizeField(memberData.role),
            expertise: normalizeField(memberData.expertise),
            bio: normalizeField(memberData.bio || memberData.description),
            photoUrl: memberData.photoUrl || memberData.photo || '',
            sortOrder: memberData.sortOrder || 0,
            isFeatured: memberData.isFeatured || false,
            isActive: memberData.isActive !== false,
        };

        const response = await fetch(`${apiUrl}/admin/team/members/${memberId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...getAuthHeaders(),
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error(`Failed to update team member. Status: ${response.status}`, data);
            return { error: true, ...data };
        }

        return data;
    } catch (error) {
        console.error("Error updating admin team member:", error);
        return { error: true, message: error.message };
    }
}

/**
 * Удалить участника команды
 * DELETE /admin/team/members/{memberId}
 */
export async function deleteAdminTeamMember(memberId) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return null;
        }

        const response = await fetch(`${apiUrl}/admin/team/members/${memberId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders(),
            },
        });

        if (response.status === 204) {
            return { ok: true };
        }

        const data = await response.json();

        if (!response.ok) {
            console.error(`Failed to delete team member. Status: ${response.status}`, data);
            return { error: true, ...data };
        }

        return data;
    } catch (error) {
        console.error("Error deleting admin team member:", error);
        return { error: true, message: error.message };
    }
}

/**
 * Экспорт участников команды в CSV
 * GET /admin/team/members?export=csv
 */
export async function exportAdminTeamCsv(params = {}) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return { error: true, message: 'API URL not configured' };
        }

        const queryParams = new URLSearchParams({ ...params, export: 'csv' });
        const url = `${apiUrl}/admin/team/members?${queryParams.toString()}`;

        const response = await fetch(url, {
            cache: 'no-store',
            headers: {
                'Accept': 'text/csv',
            }
        });

        if (!response.ok) {
            console.error(`Failed to export team members. Status: ${response.status}`);
            return { error: true, message: 'Failed to export CSV' };
        }

        // Получаем blob и создаём download
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `team-members-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);

        return { ok: true };
    } catch (error) {
        console.error("Error exporting team members CSV:", error);
        return { error: true, message: error.message };
    }
}
